import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { socket } from '../../admin/chat/socket';
import {
  X,
  Truck,
  Package,
  Warehouse,
  Zap,
  RefreshCw,
  Loader2,
  User,
  Clock,
  Radio,
} from 'lucide-react';
import DateSelector from './DateSelector'; // adjust path as needed

const API_URL = process.env.REACT_APP_API_URL;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getTodayLocal() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatTime(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
}

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  with_driver: {
    label: 'Con repartidor',
    bg: 'bg-[#67A43C]/12',
    text: 'text-[#67A43C]',
    border: 'border-[#67A43C]/25',
    dot: 'bg-[#67A43C]',
    pulse: true,
  },
  in_fastmax: {
    label: 'En Fast Max',
    bg: 'bg-[#E6B43C]/12',
    text: 'text-[#B8860B]',
    border: 'border-[#E6B43C]/30',
    dot: 'bg-[#E6B43C]',
    pulse: false,
  },
  in_warehouse: {
    label: 'En almacén',
    bg: 'bg-slate-100',
    text: 'text-slate-500',
    border: 'border-slate-200',
    dot: 'bg-slate-400',
    pulse: false,
  },
  sin_paquete: {
    label: 'Sin paquete',
    bg: 'bg-slate-50',
    text: 'text-slate-400',
    border: 'border-slate-150',
    dot: 'bg-slate-300',
    pulse: false,
  },
};

function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['sin_paquete'];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? 'animate-pulse' : ''}`}
      />
      {cfg.label}
    </span>
  );
}

// ─── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color, pulse }) {
  return (
    <div className={`flex flex-col items-center justify-center rounded-xl p-3 border ${color.bg} ${color.border} min-w-0`}>
      <div className={`flex items-center gap-1 mb-1 ${color.text}`}>
        <Icon className="w-3.5 h-3.5" />
        {pulse && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
      </div>
      <p className={`text-xl font-extrabold font-montserrat ${color.text} leading-none`}>
        {value}
      </p>
      <p className="text-[10px] text-slate-500 font-montserrat mt-0.5 text-center leading-tight">
        {label}
      </p>
    </div>
  );
}

// ─── Delivery row ─────────────────────────────────────────────────────────────
function DeliveryRow({ delivery, isNew }) {
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-0 transition-all duration-500 ${
        isNew ? 'bg-[#67A43C]/5' : 'bg-white hover:bg-slate-50/60'
      }`}
    >
      {/* ID */}
      <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded shrink-0">
        #{delivery.delivery_point_id}
      </span>

      {/* Recipient */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">
          {delivery.recipient_name || '—'}
        </p>
        {delivery.driver_name && (
          <p className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
            <User className="w-3 h-3" />
            {delivery.driver_name}
          </p>
        )}
      </div>

      {/* Right: status + time */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        <StatusPill status={delivery.package_status} />
        {delivery.scanned_at && (
          <span className="flex items-center gap-1 text-[10px] text-slate-400">
            <Clock className="w-2.5 h-2.5" />
            {formatTime(delivery.scanned_at)}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export function LiveDeliveriesModal({ isOpen, onClose, userId }) {
  const [selectedDate, setSelectedDate] = useState(getTodayLocal());
  const [showCalendar, setShowCalendar] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [totals, setTotals] = useState({ with_driver: 0, in_fastmax: 0, in_warehouse: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [liveActive, setLiveActive] = useState(false);
  const [newlyUpdatedIds, setNewlyUpdatedIds] = useState(new Set());

  // ── Fetch deliveries for selected date ──────────────────────────────────────
  const fetchDeliveries = useCallback(async () => {
    if (!userId || !selectedDate) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/packages/deliveries-by-date`, {
        params: { user_id: userId, date: selectedDate },
      });
      setDeliveries(data.deliveries || []);
      setTotals(data.totals || { with_driver: 0, in_fastmax: 0, in_warehouse: 0, total: 0 });
    } catch (err) {
      console.error('Error fetching deliveries by date:', err);
      setError('No se pudo cargar las entregas. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [userId, selectedDate]);

  // Fetch on open or date change
  useEffect(() => {
    if (isOpen) fetchDeliveries();
  }, [isOpen, fetchDeliveries]);

  // ── Socket.IO — attach listeners on open, detach on close ───────────────────
  useEffect(() => {
    if (!isOpen) return;

    // Join the global package updates room
    socket.emit('join_package_updates');
    setLiveActive(true);

    // Handle single scan update
    const handleSingleUpdate = (data) => {
      // Only apply live updates when viewing today
      const today = getTodayLocal();
      if (selectedDate !== today) return;

      setDeliveries((prev) => {
        const updated = prev.map((d) =>
          d.delivery_point_id === data.delivery_point_id
            ? {
                ...d,
                package_status: data.new_status,
                driver_name: data.driver_name || d.driver_name,
                scanned_at: data.scanned_at,
              }
            : d
        );

        // Recalculate totals inline so we always work from latest state
        const statuses = updated.map((d) => d.package_status);
        setTotals({
          with_driver: statuses.filter((s) => s === 'with_driver').length,
          in_fastmax: statuses.filter((s) => s === 'in_fastmax').length,
          in_warehouse: statuses.filter((s) => s === 'in_warehouse').length,
          total: updated.length,
        });

        return updated;
      });

      // Flash the updated row green for 3 seconds
      setNewlyUpdatedIds((prev) => new Set([...prev, data.delivery_point_id]));
      setTimeout(() => {
        setNewlyUpdatedIds((prev) => {
          const next = new Set(prev);
          next.delete(data.delivery_point_id);
          return next;
        });
      }, 3000);
    };

    // Handle bulk scan — process each package individually
    const handleBulkUpdate = (data) => {
      if (!data.packages) return;
      data.packages.forEach((pkg) => handleSingleUpdate(pkg));
    };

    socket.on('package_status_updated', handleSingleUpdate);
    socket.on('package_bulk_updated', handleBulkUpdate);

    // Cleanup: remove listeners when modal closes (don't disconnect shared socket)
    return () => {
      socket.off('package_status_updated', handleSingleUpdate);
      socket.off('package_bulk_updated', handleBulkUpdate);
      setLiveActive(false);
    };
  }, [isOpen, selectedDate]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[88vh]">

          {/* ── Header ────────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#B1336E]/10 flex items-center justify-center">
                <Truck className="w-5 h-5 text-[#B1336E]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-extrabold text-slate-800 font-montserrat leading-tight">
                    Seguimiento de Entregas
                  </h2>
                  {liveActive && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#67A43C]/10 text-[#67A43C] border border-[#67A43C]/20">
                      <Radio className="w-2.5 h-2.5" />
                      EN VIVO
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400">
                  {loading ? 'Cargando...' : `${totals.total} puntos de entrega`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchDeliveries}
                disabled={loading}
                className="p-2 text-slate-400 hover:text-[#B1336E] hover:bg-[#B1336E]/5 rounded-lg transition-colors disabled:opacity-40"
                title="Actualizar"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* ── Date picker ───────────────────────────────────────────────── */}
          <div className="px-5 pt-4 pb-2 shrink-0">
            <DateSelector
              selectedDate={selectedDate}
              onDateChange={(date) => {
                setSelectedDate(date);
                setShowCalendar(false);
              }}
              showCalendar={showCalendar}
              onToggleCalendar={() => setShowCalendar((v) => !v)}
            />
          </div>

          {/* ── Stat cards ────────────────────────────────────────────────── */}
          <div className="px-5 py-3 shrink-0">
            <div className="grid grid-cols-3 gap-2">
              <StatCard
                icon={Truck}
                label="Con repartidor"
                value={totals.with_driver}
                color={{ bg: 'bg-[#67A43C]/8', border: 'border-[#67A43C]/20', text: 'text-[#67A43C]' }}
                pulse={liveActive}
              />
              <StatCard
                icon={Zap}
                label="En Fast Max"
                value={totals.in_fastmax}
                color={{ bg: 'bg-[#E6B43C]/10', border: 'border-[#E6B43C]/25', text: 'text-[#B8860B]' }}
                pulse={false}
              />
              <StatCard
                icon={Warehouse}
                label="En almacén"
                value={totals.in_warehouse}
                color={{ bg: 'bg-slate-100', border: 'border-slate-200', text: 'text-slate-500' }}
                pulse={false}
              />
            </div>
          </div>

          {/* ── Deliveries list ───────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto border-t border-slate-100">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-8 h-8 text-[#B1336E] animate-spin" />
                <p className="text-sm text-slate-500">Cargando entregas...</p>
              </div>
            )}

            {!loading && error && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <p className="text-sm text-red-500">{error}</p>
                <button
                  onClick={fetchDeliveries}
                  className="text-xs text-[#B1336E] underline"
                >
                  Reintentar
                </button>
              </div>
            )}

            {!loading && !error && deliveries.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                  <Package className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-sm font-semibold text-slate-600">Sin entregas para esta fecha</p>
                <p className="text-xs text-slate-400 mt-1">
                  Selecciona otra fecha o espera que se asignen entregas
                </p>
              </div>
            )}

            {!loading && !error && deliveries.length > 0 && (
              <div className="divide-y divide-slate-100">
                {deliveries.map((delivery) => (
                  <DeliveryRow
                    key={delivery.delivery_point_id}
                    delivery={delivery}
                    isNew={newlyUpdatedIds.has(delivery.delivery_point_id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Footer ────────────────────────────────────────────────────── */}
          {!loading && !error && deliveries.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100 shrink-0 bg-slate-50/60 rounded-b-2xl">
              <p className="text-xs text-slate-400 text-center">
                {liveActive
                  ? 'Actualizando en tiempo real cuando el repartidor escanea'
                  : 'Conecta para ver actualizaciones en vivo'}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}